// What the seven record pages share (U7, B165): the page heading with its counts, the
// filter pills as the artboards draw them, the table, the "when" and channel words, and
// the plain record a row of the undrawn pages opens into. Pure helpers are exported so the
// words on the page are tested without a DOM (records/format.test.ts).
import { HowCobrainReads } from './HowCobrainReads.js';
import { CardFrame, EmptyState, ActionButton, ActionSelect } from '../parts/index.js';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Unauthenticated, type LastContact, type Store } from '../api.js';
import { LIVE_COALESCE_MS, useLiveRefresh } from '../live.js';
import { formatDate, formatWhen } from '../labels.js';
import { paperLink, recordFields, type RecordField } from './record-fields.js';
import './records.css';

/** The channel words, as Recent.tsx spells them. */
export const KINDS: Record<string, string> = {
  email: 'Email', teams_chat: 'Teams chat', teams_channel: 'Teams channel', cobrain_chat: 'Cobrain',
  whatsapp: 'WhatsApp', call: 'Call', meeting: 'Meeting', calendar_event: 'Calendar', document: 'Document',
  note: 'Note', news_item: 'News', digest: 'Digest', span_extract: 'Extract', manual_note: 'Note',
};
export const kindWord = (kind: string): string => KINDS[kind] ?? kind;

/** "today", "3 days ago", or the date: the artboards' when-stamps. */
export function whenWord(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const w = formatWhen(iso, now);
  if (w.kind === 'absolute') return formatDate(w.iso);
  if (w.text === 'just now' || w.text.endsWith('min ago') || w.text.endsWith('hour ago') || w.text.endsWith('hours ago')) return 'today';
  return w.text;
}

/** "4 Sep, email": the last-contact cell; empty when nothing recent was seen. */
export function contactWord(c: LastContact | undefined, now: number = Date.now()): string {
  return c ? `${whenWord(c.at, now)}, ${kindWord(c.kind).toLowerCase()}` : '';
}

/** The tier word: "Global Tier 1" as the sheet writes it, or the artboard's bracket text. */
export const orText = (v: string | number | null | undefined, empty = ''): string => (v === null || v === undefined || v === '' ? empty : String(v));

export const ROUTE_WORDS: Record<string, string> = { igm_live: 'IGM Live', genius: 'Genius', both: 'IGM Live and Genius', integrating: 'Integrating' };
export const routeWord = (v: string | null): string => (v ? ROUTE_WORDS[v] ?? v : '');
export const TYPE_WORDS: Record<string, string> = { client: 'Client', prospect: 'Prospect' };
export const typeWord = (v: string | null): string => (v ? TYPE_WORDS[v] ?? v : '');
export const CLASS_WORDS: Record<string, string> = {
  client: 'Client', prospect: 'Prospect', supplier: 'Supplier', rights_holder: 'Rights holder', partner: 'Partner', internal: 'Internal',
  solicitor: 'Solicitor', counsel: 'Counsel', school: 'School', clinician: 'Clinician', family: 'Family', personal_vendor: 'Personal vendor', personal_finance: 'Personal finance', other: 'Other',
};
export const classWord = (v: string): string => CLASS_WORDS[v] ?? v;
export const gbp = (n: number | null): string => (n === null ? '' : n.toLocaleString('en-GB', { maximumFractionDigits: 0 }));
export const STATUS_WORDS: Record<string, string> = { suggested: 'Suggested', accepted: 'Accepted', in_progress: 'In progress', scheduled: 'Scheduled', delegated: 'Delegated', parked: 'Parked', done: 'Done', dismissed: 'Dismissed', merged: 'Merged' };
export const statusWord = (v: string): string => STATUS_WORDS[v] ?? v;
export const KIND_WORDS: Record<string, string> = { deal: 'Deal', relationship: 'Relationship', project: 'Project', personal: 'Personal', legal: 'Legal', other: 'Other' };
export const matterKindWord = (v: string): string => KIND_WORDS[v] ?? v;
export const ORIGIN_WORDS: Record<string, string> = { pitched: 'Pitched', enquiry: 'Enquiry', referral: 'Referral' };
export const originWord = (v: string | null): string => (v ? ORIGIN_WORDS[v] ?? v : '');
/** Where a task came from, in words rather than in the store's own vocabulary. */
export function fromWord(createdFrom: string, evidence: number, ownerName: string | null): string {
  if (createdFrom === 'inferred') return `read from ${evidence} item${evidence === 1 ? '' : 's'}`;
  if (createdFrom === 'manual') return `added by hand${ownerName ? `, ${ownerName.split(' ')[0]}` : ''}`;
  if (createdFrom === 'seed_asana') return 'brought in from Asana';
  if (createdFrom === 'delegation') return 'delegated';
  return createdFrom;
}

/** The four-segment positivity scale (D116), filled to the nearest quarter. */
export function Scale({ v }: { v: number | null }) {
  if (v === null) return null;
  const filled = Math.max(0, Math.min(4, Math.round(v * 4)));
  return <><span className="scale" title={`${Math.round(v * 100)}% positive`}>{[0, 1, 2, 3].map((i) => <i key={i} className={i < filled ? 'f' : ''} />)}</span><span className="scale-n">{filled} of 4</span></>;
}

/** A filter chip: "Clients 152". Counts come from the API, never from a constant. */
export function Pill({ on, onClick, children, n }: { on: boolean; onClick: () => void; children: ReactNode; n?: number | null | undefined }) {
  return <ActionButton reading type="button" className={on ? 'tg on' : 'tg'} aria-pressed={on} onClick={onClick}>{children}{n === undefined || n === null ? null : <em>{n.toLocaleString('en-GB')}</em>}</ActionButton>;
}

/** A "By x" chip with a select inside it; the chip draws the caret, so the label does not. */
export function Facet({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <label className={value ? 'tg on' : 'tg'}>
      <ActionSelect reading aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">{label}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </ActionSelect>
    </label>
  );
}

export type Load<T> = { kind: 'loading' } | { kind: 'ready'; data: T } | { kind: 'error'; message: string };

/**
 * Load through the API, and send a signed-out caller to sign in -- and read again when the
 * person comes back to the page (B1260, adopting the policy of live.ts, B914).
 *
 * This one loader is nine surfaces: the six record lists, the record a row opens into, the
 * company record and the item page. Each of them read once when it mounted and then sat there,
 * so a tab left open on Companies while the person worked in Teams showed the store as it stood
 * when they last looked. That is what Mike reported on 2026-09-21 as a bug on ALL pages: seven
 * surfaces had taken the policy by then and these had not.
 *
 * No beat, only the return (live.ts rule 2: no beat on a read nobody has timed). These reads
 * walk the register and the correspondence and not one of them has been measured on his live
 * store, so putting them on a minute's clock would be guessing with the store's own capacity.
 * The return alone is what the fault actually was.
 */
export function useLoad<T>(fetcher: () => Promise<T>, deps: unknown[], opts: { keep?: boolean } = {}): [Load<T>, () => void, boolean] {
  const [state, setState] = useState<Load<T>>({ kind: 'loading' });
  const [busy, setBusy] = useState(false);
  const [n, setN] = useState(0);
  const keep = opts.keep ?? false;
  // The fetcher is a new closure on every render; the policy attaches its listeners once, so it
  // reads the latest through a ref rather than re-attaching them each time (live.ts, useLiveRefresh).
  const latest = useRef(fetcher);
  latest.current = fetcher;
  // Attention's watcher only tracks its own reads, not the effect's initial load or reload.
  const effectLoad = useRef({ inFlight: false, startedAt: Number.NEGATIVE_INFINITY });

  useEffect(() => {
    let cancelled = false;
    const attempt = { inFlight: true, startedAt: Date.now() };
    effectLoad.current = attempt;
    // A list being narrowed (`keep`, pipeline F1, B1950) keeps what it shows until the narrower answer lands, so the
    // table does not drop to a skeleton at every pause in typing; `busy` says a read is out. Anything else, and the
    // first read, shows the skeleton as before.
    setState((s) => (keep && s.kind === 'ready' ? s : { kind: 'loading' }));
    setBusy(true);
    fetcher()
      .then((data) => { if (!cancelled) setState({ kind: 'ready', data }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) });
      })
      .finally(() => { attempt.inFlight = false; if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);

  /**
   * The re-read. It does NOT set `loading` first: the list stays on the screen and is replaced
   * when the answer lands, because blanking a page to a skeleton every time somebody alt-tabs
   * back to it is a worse page than a stale one (B1042 learned this on triage and meetings).
   * It hands back the promise, which is what buys it the policy's one-at-a-time guard and its
   * deadline; a loader that returns anything else gets neither.
   */
  const quiet = useCallback((signal: AbortSignal) => {
    if (effectLoad.current.inFlight || Date.now() - effectLoad.current.startedAt < LIVE_COALESCE_MS) return;
    return latest.current().then(
      (data) => { if (!signal.aborted) setState({ kind: 'ready', data }); },
      (e: unknown) => {
        if (signal.aborted) return;
        if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
        // A read that does not land leaves what is on the screen. The page was working a moment
        // ago; one failed re-read is not a reason to replace it with an error.
      },
    );
  }, []);
  useLiveRefresh(quiet);

  return [state, () => setN((x) => x + 1), busy];
}

export function Loading({ what }: { what: string }) {
  return <div className="rec-skel" role="status" aria-label={`Reading ${what}`}><i /><i /><i /><i /><i /><i /></div>;
}
export function Failed({ message }: { message: string }) {
  return <p className="rec-failed" role="alert">Cobrain could not read that just now. {message}</p>;
}

/** The page head: the title, then the counts. */
export function Heading({ title, line, children }: { title: string; line: ReactNode; children?: ReactNode }) {
  return <div className="rec-top"><h2>{title}</h2><span className="rec-counts">{line}</span>{children}</div>;
}

/** One count, in score numerals: "152 on record". */
export function Count({ n, children }: { n: number | null | undefined; children: ReactNode }) {
  if (n === null || n === undefined) return null;
  return <span><b className="big">{n.toLocaleString('en-GB')}</b> {children}</span>;
}

/** The foot of a table: where you are in it, and how to move. */
/**
 * A record's name in a list, as a real link (BUGS.md D6): a plain press opens it in the page as the row does; a press with
 * a modifier, a middle press or "Open in new tab" opens the address in a new tab, which a button never could. The row's
 * own press is not fired under it either way. Drawn as the text it was: records.css `.recs a.row-link`.
 */
export function RowLink({ to, open, className = 'row-link', children }: { to: string; open: (path: string) => void; className?: string; children: ReactNode }) {
  return (
    <a href={to} className={className} onClick={(e) => {
      e.stopPropagation();
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      open(to);
    }}>{children}</a>
  );
}

export function Foot({ cols, offset, limit, total, shown, onPage }: {
  cols: number; offset: number; limit: number; total: number; shown?: number; onPage: ((next: number) => void) | null;
}) {
  const drawn = shown ?? limit;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + drawn, total);
  return (
    <tr><td colSpan={cols} style={{ padding: 0 }}>
      <div className="rec-foot">
        <span>{onPage ? `${from.toLocaleString('en-GB')} to ${to.toLocaleString('en-GB')} of ${total.toLocaleString('en-GB')}` : `${(to - from + (total === 0 ? 0 : 1)).toLocaleString('en-GB')} shown of ${total.toLocaleString('en-GB')}`}</span>
        <span className="sp" />
        {onPage && offset > 0 ? <ActionButton reading type="button" className="dotted" onClick={() => onPage(Math.max(0, offset - limit))}>Previous {limit}</ActionButton> : null}
        {onPage && offset + limit < total ? <ActionButton reading type="button" className="dotted" onClick={() => onPage(offset + limit)}>Next {limit}</ActionButton> : null}
      </div>
    </td></tr>
  );
}

/** Nothing matched, or nothing is there yet: one line, and what would fill it. */
export function Empty({ cols, head, says }: { cols: number; head: string; says: string }) {
  return <EmptyState columns={cols} title={head} note={says} />;
}

export interface PageProps { store: Store; open: (path: string) => void }

/** A person's page, in the store that holds them: the commercial store's needs no name (BUGS.md D74). */
export function personPath(id: string, store?: string | null): string {
  return `/people/${id}${store ? `?store=${encodeURIComponent(store)}` : ''}`;
}
/** Everything Cobrain has read about one person, in the store that holds them (BUGS.md D78). */
export function personReadPath(id: string, store?: string | null): string {
  return `/read?person=${encodeURIComponent(id)}${store ? `&store=${encodeURIComponent(store)}` : ''}`;
}

/** The record a row of People, Deals or Matters opens into: its own fields (record-fields.ts; Contracts and Rights are worded there). */
export { fieldsOf } from './record-fields.js';

export function SimpleRecord({ store, page, id, title, back, open }: { store: Store; page: string; id: string; title: string; back: string; open: (path: string) => void }) {
  const [state] = useLoad(() => import('../api.js').then((m) => m.records.record<Record<string, unknown>>(store.tenantId, page, id)), [store.tenantId, page, id]);
  const paper = state.kind === 'ready' ? paperLink(page, state.data.record) : null;
  return (
    <div className="recs">
      <p className="back-line"><ActionButton reading type="button" className="dotted" onClick={() => open(back)}>← {title}</ActionButton></p>
      {state.kind === 'loading' ? <Loading what="the record" /> : state.kind === 'error' ? <Failed message={state.message} /> : (
        <CardFrame variant="panel" >
          <h3>{String(state.data.record.title ?? state.data.record.name ?? state.data.record.matterTitle ?? state.data.record.propertyName ?? state.data.record.counterpartyName ?? '')}</h3>
          <dl className="kv">
            {recordFields(page, state.data.record).map((f) => <FieldRow key={f.label} field={f} open={open} />)}
          </dl>
          {page === 'people'
            // BUGS.md D78: the person's own items, in the store that holds them, not the store's newest.
            ? <p className="note" style={{ marginTop: 10 }}><ActionButton reading type="button" className="dotted" onClick={() => open(personReadPath(id, store.tenantId))}>Everything Cobrain has read about them</ActionButton></p>
            : null}
          {page === 'people' ? <HowCobrainReads store={store.tenantId} principalId={typeof state.data.record.principalId === 'string' ? state.data.record.principalId : null} name={String(state.data.record.name ?? '')} /> : null}
          {page === 'rights' ? <RightLinks record={state.data.record} open={open} /> : null}
          {/* BUGS.md T37: the signed agreement opens where the company record's agreement card opens it; no paper, no link. */}
          {paper
            ? <p className="note" style={{ marginTop: 10 }}><a className="dotted" href={paper} target="_blank" rel="noreferrer">Open the signed agreement</a></p>
            : null}
        </CardFrame>
      )}
    </div>
  );
}

/**
 * BUGS.md D129 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "A right's page names its deal and the
 * operators on its roll-out"): its rights deals, each opening the deal, and the operators on their roll-out, each opening
 * its company where it is on record. An older API sends neither, and nothing is drawn.
 */
export function RightLinks({ record, open }: { record: Record<string, unknown>; open: (path: string) => void }) {
  const deals = Array.isArray(record.deals) ? (record.deals as Array<{ id: string; title: string }>) : [];
  const operators = Array.isArray(record.operators) ? (record.operators as Array<{ organisationId: string | null; name: string }>) : [];
  if (deals.length === 0 && operators.length === 0) return null;
  return (
    <>
      {deals.length ? (
        <p className="note" style={{ marginTop: 10 }}>
          <b>{deals.length === 1 ? 'Its deal' : 'Its deals'}:</b>{' '}
          {deals.map((d, i) => <span key={d.id}>{i ? '; ' : ''}<ActionButton reading type="button" className="dotted" onClick={() => open(`/deals/${d.id}`)}>{d.title}</ActionButton></span>)}
        </p>
      ) : null}
      {operators.length ? (
        <p className="note" style={{ marginTop: 6 }}>
          <b>On its roll-out:</b>{' '}
          {operators.map((o, i) => <span key={`${o.organisationId ?? ''}${o.name}`}>{i ? ', ' : ''}{o.organisationId ? <ActionButton reading type="button" className="dotted" onClick={() => open(`/companies/${o.organisationId}`)}>{o.name}</ActionButton> : o.name}</span>)}
        </p>
      ) : null}
    </>
  );
}

/** One field: a company it names opens that company's record, as the Contracts and Rights tables open it (T37, T45). */
function FieldRow({ field: f, open }: { field: RecordField; open: (path: string) => void }) {
  const value = f.value && f.companyId
    ? <ActionButton reading type="button" className="dotted" onClick={() => open(`/companies/${f.companyId}`)}>{f.value}</ActionButton>
    : f.value;
  return <><dt>{f.label}</dt><dd>{value || <span className="note">—</span>}{f.note ? <> <span className="note">{f.note}</span></> : null}</dd></>;
}
